import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { media } from '../../styles/theme';
import { getMyTimetable } from '../../api/timetableApi';
import {
  getWeekStart,
  getWeekEnd,
  formatWeekRange,
  formatDateShort,
  formatDateYMD,
  getDateForDayInWeek
} from '../../api/courseGroupPortalApi';

// Constants
const TIME_START = 8; // 8:00 AM
const TIME_END = 22; // 10:00 PM
const HOUR_HEIGHT = 50; // pixels per hour
const DAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' }
];

// Color palette for courses
const COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6',
  '#ef4444', '#06b6d4', '#84cc16', '#ec4899',
  '#6366f1', '#14b8a6', '#f97316', '#a855f7'
];

// Styled Components
const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const TimetableWrapper = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  /* 手机/平板：8 列周表挤不下，改为横向滚动 */
  ${media.tablet} {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const TimetableGrid = styled.div`
  display: grid;
  grid-template-columns: 60px repeat(7, 1fr);
  min-height: ${(TIME_END - TIME_START) * HOUR_HEIGHT + 48}px;

  /* 横向滚动时给每天一个可读最小宽度 */
  ${media.tablet} {
    grid-template-columns: 48px repeat(7, minmax(96px, 1fr));
    min-width: 720px;
  }
`;

const HeaderRow = styled.div`
  display: contents;
`;

const TimeHeader = styled.div`
  background: #f9fafb;
  padding: 12px 8px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  text-align: center;
`;

const DayHeader = styled.div`
  background: #10b981;
  color: white;
  padding: 12px;
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 1px solid #e5e7eb;
`;

const TimeColumn = styled.div`
  background: #f9fafb;
  border-right: 1px solid #e5e7eb;
`;

const TimeSlot = styled.div`
  height: ${HOUR_HEIGHT}px;
  padding: 4px 8px;
  font-size: 11px;
  color: #9ca3af;
  text-align: right;
  border-bottom: 1px solid #f3f4f6;
`;

const DayColumn = styled.div`
  position: relative;
  border-right: 1px solid #e5e7eb;
  min-height: ${(TIME_END - TIME_START) * HOUR_HEIGHT}px;

  &:last-child {
    border-right: none;
  }
`;

const HourLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: ${props => props.$top}px;
  border-top: 1px solid #f3f4f6;
  pointer-events: none;
`;

const CourseBlock = styled.div`
  position: absolute;
  left: ${props => props.$left || '2px'};
  right: 2px;
  top: ${props => props.$top}px;
  height: ${props => props.$height}px;
  width: ${props => props.$width || 'calc(100% - 4px)'};
  background: ${props => props.$color};
  border-radius: 6px;
  padding: 6px 8px;
  color: white;
  font-size: 11px;
  overflow: hidden;
  cursor: pointer;
  z-index: ${props => props.$zIndex || 1};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: transform 0.1s, box-shadow 0.1s;

  ${props => props.$cancelled && `
    opacity: 0.55;
    background: repeating-linear-gradient(
      135deg, ${props.$color}, ${props.$color} 6px, rgba(255, 255, 255, 0.25) 6px,
      rgba(255, 255, 255, 0.25) 9px
    );
    text-decoration: line-through;
  `}

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 100;
  }
`;

const CourseTitle = styled.div`
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
`;

const CourseTime = styled.div`
  font-size: 10px;
  opacity: 0.9;
`;

const CourseCampus = styled.div`
  font-size: 10px;
  opacity: 0.9;
  margin-top: 2px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.2);
  padding: 1px 4px;
  border-radius: 3px;
  display: inline-block;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  padding: 24px;
  background: #fef2f2;
  border-radius: 8px;
  color: #dc2626;
  margin: 24px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #6b7280;
  text-align: center;
  padding: 48px;

  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  h3 {
    font-size: 18px;
    font-weight: 500;
    margin: 0 0 8px 0;
    color: #374151;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;

const StatsBar = styled.div`
  display: flex;
  gap: 24px;
  padding: 16px 24px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #4b5563;

  strong {
    color: #1f2937;
  }
`;

const WeekNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const WeekNavButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    border-color: #10b981;
  }
`;

const WeekDisplay = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  min-width: 200px;
  text-align: center;
`;

const TodayButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #10b981;
  border-radius: 6px;
  background: white;
  color: #10b981;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #10b981;
    color: white;
  }
`;

// Utility functions
const calculatePosition = (startTime, endTime) => {
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  const startDecimal = startH + startM / 60;
  const endDecimal = endH + endM / 60;
  return {
    top: (startDecimal - TIME_START) * HOUR_HEIGHT,
    height: Math.max((endDecimal - startDecimal) * HOUR_HEIGHT, 20)
  };
};

// Room is free text and campuses spell it differently: Claremont stores bare numbers ('3'),
// Vic Park stores 'Room 3'. A bare '3' next to the campus name reads like a year level, so
// numbers get the prefix. A schedule row's own room wins over the course group's default —
// the same precedence the campus timetable uses.
const formatRoom = (raw) => {
  const room = String(raw || '').trim();
  if (!room) return '';
  return /^\d+$/.test(room) ? `Room ${room}` : room;
};

// Short labels for a class that is not simply going ahead. A cancelled class stays on the
// grid (struck through) rather than vanishing, so the teacher can see it was called off.
const STATUS_LABELS = {
  cancelled: 'Cancelled',
  pending: 'Pending',
  no_show_student: 'Student no-show',
  no_show_teacher: 'Teacher no-show'
};

// The backend has already expanded the week (term dates, holidays, relief, single-week
// changes); all that is left here is placing each occurrence on the grid.
const transformOccurrencesToEvents = (occurrences) => {
  const colorMap = new Map();

  return occurrences
    .filter(occ => occ.dayOfWeek && occ.startTime && occ.endTime)
    .map(occ => {
      // One colour per course group; a standalone lesson (trial, assessment) gets its own
      const colorKey = occ.courseGroupId || occ.id;
      if (!colorMap.has(colorKey)) {
        colorMap.set(colorKey, COLORS[colorMap.size % COLORS.length]);
      }
      const position = calculatePosition(occ.startTime, occ.endTime);

      return {
        id: occ.id,
        courseGroupId: occ.courseGroupId,
        dayOfWeek: occ.dayOfWeek,
        date: occ.date,
        startTime: occ.startTime,
        endTime: occ.endTime,
        subject: occ.title,
        year: occ.year,
        campusName: occ.campusName,
        campusId: occ.campusId,
        room: formatRoom(occ.room),
        status: occ.status,
        statusLabel: STATUS_LABELS[occ.status] || '',
        color: colorMap.get(colorKey),
        top: position.top,
        height: position.height
      };
    });
};

const handleOverlaps = (events) => {
  // Group events by day
  const byDay = {};
  events.forEach(event => {
    const day = event.dayOfWeek;
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(event);
  });

  // For each day, detect and handle overlaps
  Object.values(byDay).forEach(dayEvents => {
    // Sort by start time
    dayEvents.sort((a, b) => a.top - b.top);

    // Find overlapping groups
    dayEvents.forEach((event, i) => {
      const overlapping = dayEvents.filter(
        other => other !== event &&
          !(other.top >= event.top + event.height ||
            other.top + other.height <= event.top)
      );

      if (overlapping.length > 0) {
        // Calculate position in overlap group
        const allInGroup = [event, ...overlapping].sort((a, b) => a.top - b.top || a.id.localeCompare(b.id));
        const myIndex = allInGroup.findIndex(e => e.id === event.id);
        const totalInGroup = allInGroup.length;
        const widthPercent = 100 / totalInGroup;

        event.$width = `calc(${widthPercent}% - 4px)`;
        event.$left = `calc(${myIndex * widthPercent}% + 2px)`;
        event.$zIndex = myIndex + 1;
      }
    });
  });

  return events;
};

const CampusTimetableTeacher = () => {
  const [occurrences, setOccurrences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Week navigation state
  const [currentWeekStart, setCurrentWeekStart] = useState(() => getWeekStart(new Date()));

  // Week end computed from week start
  const currentWeekEnd = useMemo(() => getWeekEnd(currentWeekStart), [currentWeekStart]);

  // Each week is fetched on its own: which classes run depends on the date (term dates,
  // holiday weeks, relief, single-week changes), so there is no one list to page through.
  useEffect(() => {
    let stale = false;
    const loadWeek = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getMyTimetable(formatDateYMD(currentWeekStart), formatDateYMD(currentWeekEnd));
        if (!stale) setOccurrences(data);
      } catch (err) {
        console.error('Error loading timetable data:', err);
        if (!stale) setError(err.message);
      } finally {
        if (!stale) setLoading(false);
      }
    };

    loadWeek();
    return () => { stale = true; };
  }, [currentWeekStart, currentWeekEnd]);

  // Week navigation handlers
  const goToWeek = (offset) => {
    setCurrentWeekStart(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + (offset * 7));
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentWeekStart(getWeekStart(new Date()));
  };

  // Check if current week is the same as today's week
  const isCurrentWeek = useMemo(() => {
    const todayWeekStart = getWeekStart(new Date());
    return currentWeekStart.getTime() === todayWeekStart.getTime();
  }, [currentWeekStart]);

  // Days with dates for the current week
  const daysWithDates = useMemo(() => {
    return DAYS.map(day => ({
      ...day,
      date: getDateForDayInWeek(currentWeekStart, day.value),
      dateLabel: formatDateShort(getDateForDayInWeek(currentWeekStart, day.value))
    }));
  }, [currentWeekStart]);

  const timetableEvents = useMemo(
    () => handleOverlaps(transformOccurrencesToEvents(occurrences)),
    [occurrences]
  );

  // Stats describe this week only, and a cancelled class is not a session taught
  const stats = useMemo(() => {
    const running = timetableEvents.filter(event => event.status !== 'cancelled');
    return {
      courseCount: new Set(running.map(event => event.courseGroupId || event.id)).size,
      campusCount: new Set(running.map(event => event.campusId)).size,
      sessionCount: running.length
    };
  }, [timetableEvents]);

  // Generate time slots
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = TIME_START; hour < TIME_END; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    return slots;
  }, []);

  if (loading) {
    return (
      <Container>
        <LoadingContainer>Loading your timetable...</LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>Error: {error}</ErrorContainer>
      </Container>
    );
  }

  const hasEvents = timetableEvents.length > 0;

  return (
    <Container>
      <Header>
        <Title>My Teaching Timetable</Title>
        <Subtitle>Your assigned classes across all campuses</Subtitle>
      </Header>

      {/* Week Navigation */}
      <WeekNavigation>
        <WeekNavButton onClick={() => goToWeek(-1)}>
          ← Previous
        </WeekNavButton>
        <WeekDisplay>
          {formatWeekRange(currentWeekStart, currentWeekEnd)}
        </WeekDisplay>
        <WeekNavButton onClick={() => goToWeek(1)}>
          Next →
        </WeekNavButton>
        {!isCurrentWeek && (
          <TodayButton onClick={goToToday}>
            Today
          </TodayButton>
        )}
      </WeekNavigation>

      <TimetableWrapper>
        {!hasEvents ? (
          <EmptyState>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3>No Classes This Week</h3>
            <p>You have no classes scheduled this week.</p>
          </EmptyState>
        ) : (
          <>
            <StatsBar>
              <StatItem>
                <strong>{stats.courseCount}</strong> Course{stats.courseCount !== 1 ? 's' : ''}
              </StatItem>
              <StatItem>
                <strong>{stats.campusCount}</strong> Campus{stats.campusCount !== 1 ? 'es' : ''}
              </StatItem>
              <StatItem>
                <strong>{stats.sessionCount}</strong> Session{stats.sessionCount !== 1 ? 's' : ''} this week
              </StatItem>
            </StatsBar>

            <TimetableGrid>
              {/* Header Row */}
              <HeaderRow>
                <TimeHeader>Time</TimeHeader>
                {daysWithDates.map(day => (
                  <DayHeader key={day.value}>{day.label} ({day.dateLabel})</DayHeader>
                ))}
              </HeaderRow>

              {/* Time Column */}
              <TimeColumn>
                {timeSlots.map(time => (
                  <TimeSlot key={time}>{time}</TimeSlot>
                ))}
              </TimeColumn>

              {/* Day Columns */}
              {daysWithDates.map(day => (
                <DayColumn key={day.value}>
                  {/* Hour lines */}
                  {timeSlots.map((_, index) => (
                    <HourLine key={index} $top={index * HOUR_HEIGHT} />
                  ))}

                  {/* Course blocks for this day */}
                  {timetableEvents
                    .filter(event => event.dayOfWeek === day.value)
                    .map(event => (
                      <CourseBlock
                        key={event.id}
                        $top={event.top}
                        $height={event.height}
                        $color={event.color}
                        $width={event.$width}
                        $left={event.$left}
                        $zIndex={event.$zIndex}
                        $cancelled={event.status === 'cancelled'}
                        title={`${event.subject}${event.year ? ` Y${event.year}` : ''}${event.statusLabel ? ` (${event.statusLabel})` : ''}\n${event.startTime} - ${event.endTime}\n${event.date}\n${event.campusName}${event.room ? ` · ${event.room}` : ''}`}
                      >
                        <CourseTitle>{event.subject}{event.year ? ` Y${event.year}` : ''}</CourseTitle>
                        <CourseTime>
                          {event.startTime} - {event.endTime}
                          {event.statusLabel && ` · ${event.statusLabel}`}
                        </CourseTime>
                        {(event.campusName || event.room) && (
                          <CourseCampus>
                            {[event.campusName, event.room].filter(Boolean).join(' · ')}
                          </CourseCampus>
                        )}
                      </CourseBlock>
                    ))}
                </DayColumn>
              ))}
            </TimetableGrid>
          </>
        )}
      </TimetableWrapper>
    </Container>
  );
};

export default CampusTimetableTeacher;
